import React, { useState } from "react";
import { ListItem, ListItemText, Grid, Typography, Divider } from "@mui/material";
import { FaBars, FaCircleMinus } from "react-icons/fa6";
import { SortableList } from "shared/components/SortableList/SortableList";
import { useDeleteAssinanteDocumento } from "hooks/groupsign/useDeleteAssinanteDocumento";
import { usePostcriarAssinanteDocumento } from "hooks/groupsign/usePostcriarAssinanteDocumento";
import MultiSelectAsync from "shared/components/select/MultiSelectAsync";
import CustomButton from "shared/components/button/CustomButton";
import { api } from "constants/RotasAPI";
import axios from "axios";
import { AssinanteDocumentoDTO } from "types/dto/groupsign/AssinanteDocumentoDTO";
import { FaPlus } from "react-icons/fa";
import TextInput from "shared/components/input/TextInput";
import { Controller } from "react-hook-form";
import { useGetConfigAssDigitalCondominioPorUuid } from "hooks/groupsign/useGetConfigAssDigitalCondominioPorUuid";
import { usePutAtualizarAssinanteDocumento } from "hooks/groupsign/usePutAtualizarAssinanteDocumento";
import { toast } from "react-hot-toast";
import { setLocale } from "yup";
import { usePermissoesContext } from "context/permissoes/PermissoesContext";
import { TipoPermissao } from "constants/EnumTipoPermissao";

const DIVIDER_STYLES = { backgroundColor: "rgba(0, 0, 0, 0.50)", width: "90%" };

interface ListaAssinantesProps {
  assinantes: AssinanteDocumentoDTO[];
  onReorder: (newOrder: AssinanteDocumentoDTO[]) => void;
  isEditing: boolean;
  configuracaoAssinanteDocumentoUuid: string;
  configuracaoAssinaturaDigitalCondominioUuid: string;
  control: any;
  trigger: any;
  register: any;
  setValue: any;
  watch: any;
  showInputEmail: boolean;
  setShowInputEmail: (showInputEmail: boolean) => void;
  setAssinanteLocalOrder: (assinante: AssinanteDocumentoDTO[]) => void
  refetch: () => void;
}
export const ListaAssinantes: React.FC<ListaAssinantesProps> = ({
  assinantes,
  onReorder,
  isEditing,
  configuracaoAssinanteDocumentoUuid,
  configuracaoAssinaturaDigitalCondominioUuid,
  control,
  trigger,
  register,
  setValue,
  watch,
  showInputEmail,
  setShowInputEmail,
  refetch,
  setAssinanteLocalOrder,
}) => {
  const [responsaveisSemEmail, setResponsaveisSemEmail] = useState<Record<number, string>>({});
  const [forceReload, setForceReload] = useState(false);
  const { mutate: deleteAssinante } = useDeleteAssinanteDocumento();
  const { mutate: postAssinante } = usePostcriarAssinanteDocumento();
  const { mutate: updateAssinante } = usePutAtualizarAssinanteDocumento();
  const { data: configAssDigitalCondominio } = useGetConfigAssDigitalCondominioPorUuid(
    configuracaoAssinaturaDigitalCondominioUuid
  );
  const { usuarioPossuiPermissao } = usePermissoesContext();
      const possuiPermissaoIncluirAssinante = usuarioPossuiPermissao(
        "configuracaoCondominio",
        TipoPermissao.INCLUIR
  );

  const condominioId = configAssDigitalCondominio?.condominioId;

  const responsaveisSelecionados = watch("select-responsavel-legal") || [];

  const handleAddAssinante = async () => {
    if (responsaveisSelecionados.length === 0) return;

    try {
      const novosAssinantesData = await Promise.all(
        responsaveisSelecionados.map(async (responsavel: any) => {
          const response = await axios.get(`${api.responsavelLegal.getResponsavelLegalPorId}/${responsavel.value}`);
          return {
            id: response.data.id,
            nome: response.data.nomeResponsavel,
            email: response.data.email,
            ordem: assinantes.length + 1,
            configuracaoAssinanteDocumentoUuid,
            responsavelLegalId: responsavel.value,
          };
        })
      );

      await Promise.all(novosAssinantesData.map((assinante) => postAssinante(assinante)));

      toast.success("Assinante adicionado(a) com sucesso.");

      setAssinanteLocalOrder([...assinantes, ...novosAssinantesData])
      setValue("select-responsavel-legal", []);
      setForceReload((prev) => !prev);
    } catch (error) {
      toast.error("Erro ao adicionar assinantes.");
    }
  };

  const handleDelete = (assinanteId: number) => {
    const novaLista = assinantes.filter((assinante) => assinante.id !== assinanteId);
    onReorder(novaLista);

    deleteAssinante(assinanteId);
    setForceReload((prev) => !prev);
  };

  const handleEmailChange = (responsavelId: number, email: string) => {
    setResponsaveisSemEmail((prev: Record<number, string>) => ({
      ...prev,
      [responsavelId]: email,
    }));
  };

  const emailValidationRules = {
    required: "E-mail é obrigatório",
    pattern: {
      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "E-mail inválido",
    },
  };

  const handleBlur = async (responsavelId: number) => {
    const email = watch(`email-${responsavelId}`) || "";

    const result = await trigger(`email-${responsavelId}`);

    if (!result) {
      return;
    }

    try {
      const updatedAssinante = assinantes.find((assinante) => assinante.responsavelLegalId === responsavelId);

      if (updatedAssinante) {
        const updatedAssinanteComEmail = { ...updatedAssinante, email, configuracaoAssinanteDocumentoUuid };


        updateAssinante(updatedAssinanteComEmail,{onSuccess: () => {refetch();}} )
        setResponsaveisSemEmail({});
        setShowInputEmail(false);
        setValue(`email-${responsavelId}`, "");
      }
    } catch (error) {
      console.log("Erro ao atualizar o e-mail.", error);
    }
  };

  const renderAssinanteItem = (assinante: any) => {
    const nome = assinante.nome;
    const email = assinante.email ?? "";

    return (
      <React.Fragment key={assinante.id}>
        <ListItem sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          {isEditing && <FaBars style={{ cursor: "grab", marginRight: 10 }} />}
          <ListItemText
            primary={
              <Typography variant="body1" fontWeight="bold" sx={{ fontSize: 14 }}>
                {nome}
              </Typography>
            }
            secondary={
              <React.Fragment key={assinante.id}>
                <Grid style={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: 12 }}>
                    {email || "Sem e-mail"}
                  </Typography>
                  {!email && isEditing && (
                    <span
                      className="link-configurar-ativo"
                      style={{
                        fontSize: 12,
                        textDecoration: "none",
                        marginLeft: 15,
                        display: "flex",
                        alignItems: "center",
                      }}
                      onClick={() => {
                        setResponsaveisSemEmail((prev) => ({ ...prev, [assinante.id]: "" }));
                        setShowInputEmail(true);
                      }}
                    >
                      <FaPlus size={12} style={{ marginRight: 5, fontSize: 12 }} />
                      Adicionar e-mail
                    </span>
                  )}
                </Grid>

                {responsaveisSemEmail[assinante.id] !== undefined && showInputEmail && isEditing && (
                  <Grid style={{ marginTop: 10, width: 300 }}>
                    <Controller
                      name={`email-${assinante.responsavelLegalId}`}
                      control={control}
                      defaultValue={responsaveisSemEmail[assinante.id] || ""}
                      rules={emailValidationRules}
                      render={({ field, fieldState }) => (
                        <>
                          <TextInput
                            id={`email-assinante-${assinante.id}`}
                            label="E-mail"
                            usarValidacaoParaFormulario={true}
                            name={`email-${assinante.id}`}
                            control={control}
                            register={register}
                            setValue={setValue}
                            onChange={(e) => {
                              field.onChange(e);
                              handleEmailChange(assinante.id, e.target.value);
                            }}
                            onBlur={() => {
                              handleBlur(assinante.responsavelLegalId);
                              field.onBlur();
                            }}
                            autoFocus
                          />
                          {fieldState.error && (
                            <Typography color="red" variant="caption" sx={{ marginTop: 1 }}>
                              {fieldState.error.message}
                            </Typography>
                          )}
                        </>
                      )}
                    />
                  </Grid>
                )}
              </React.Fragment>
            }
          />
          {isEditing && (
            <FaCircleMinus
              size={12}
              color="red"
              style={{ cursor: "pointer", marginLeft: 10, padding: 0 }}
              onClick={() => {
                if (assinante.id !== undefined) {
                  handleDelete(assinante.id);
                }
              }}
            />
          )}
        </ListItem>
        {isEditing && <Divider sx={DIVIDER_STYLES} />}
      </React.Fragment>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {isEditing && (
          <Grid container alignItems="stretch" spacing={2} sx={{ width: 500, marginLeft: 4 }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: 12, marginTop: "10px", marginLeft: "15px" }}>
              Todas as informações inseridas estão sendo gravadas automaticamente.
            </Typography>
            <Grid item xs={9}>
              <MultiSelectAsync
                id="select-responsavel-legal"
                name="select-responsavel-legal"
                label="Buscar assinante"
                endpoint={`/api/responsavel-legal/select/vigente?condominioId=${condominioId}`}
                nomeAtributoLabel="nome"
                nomeAtributoValue="id"
                control={control}
                usarValidacaoParaFormulario={false}
                transformResponse={(data) => {
                  const idsJaAdicionados = assinantes.map((res) => res.responsavelLegalId);
                  return data.filter((item: any) => !idsJaAdicionados.includes(item.id));
                }}
                onChange={(value) => {
                  setValue("select-responsavel-legal", value);
                }}
                atributosRefresh={[forceReload]}
              />
            </Grid>
            <Grid item xs={3} style={{ marginTop: 25 }}>
            {possuiPermissaoIncluirAssinante && (  
              <CustomButton
                id="botao-add-assinante"
                label="Adicionar"
                className="btn-sm border-radios"
                onClick={handleAddAssinante}
              />
            )}
            </Grid>
            <Grid item sx={{ marginTop: 5, marginLeft: 1 }}>
              <Typography variant="body1" fontWeight="bold" sx={{ fontSize: 12 }}>
                Todos os assinantes
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: 12 }}>
                Organize os assinantes na ordem desejada para o processo de assinatura.
              </Typography>
            </Grid>
          </Grid>
        )}
      </Grid>

      <Grid item xs={12} sx={{ width: 500, marginLeft: 4 }}>
        <SortableList
          key={assinantes.length}
          items={assinantes ?? []}
          itemKey="id"
          onReorder={isEditing ? onReorder : () => {}}
          renderItem={renderAssinanteItem}
          sx={{ width: 500 }}
          isMovable={isEditing}
        />
      </Grid>
    </Grid>
  );
};
